/**
 * Twins Agent Service
 *
 * DB read/write for twins_clusters — one row per detected grouping. The agent's
 * settings live in agent_settings (agent_type 'twins', migration 940; DEP-037
 * retired twins_agent_config). Proposals are written only through
 * propose_item_cluster, from the browser and the server runtime alike.
 */

import { getSupabaseClient } from './supabaseClient';

const CLUSTERS_TABLE = 'twins_clusters';

// ── Types ─────────────────────────────────────────────────────────────────────

export type TwinsClusterType   = 'duplicate' | 'variant';
export type TwinsClusterStatus = 'active' | 'accepted' | 'dismissed';

export interface TwinsCluster {
  id: string;
  workspaceId: string;
  clusterType: TwinsClusterType;
  /** Item UUIDs in this cluster */
  itemIds: string[];
  rationale: string;
  status: TwinsClusterStatus;
  proposedByAgent: string;
  createdAt: string;
  reviewedAt?: string | null;
  reviewedByUserId?: string | null;
  /**
   * The item-relationship cluster accepting this proposal created (mig 765) —
   * the unresolved cluster to resolve as duplicate / variant / reframe.
   */
  relationshipGroupId?: string | null;
}

// ── Converters ────────────────────────────────────────────────────────────────

function rowToCluster(row: Record<string, unknown>): TwinsCluster {
  const rawIds = row.item_ids;
  const itemIds: string[] = Array.isArray(rawIds)
    ? rawIds
    : typeof rawIds === 'string'
    ? JSON.parse(rawIds)
    : [];

  return {
    id:                row.id as string,
    workspaceId:       row.workspace_id as string,
    clusterType:       row.cluster_type as TwinsClusterType,
    itemIds,
    rationale:         (row.rationale as string) ?? '',
    status:            row.status as TwinsClusterStatus,
    proposedByAgent:   (row.proposed_by_agent as string) ?? 'twins',
    createdAt:         row.created_at as string,
    reviewedAt:        (row.reviewed_at as string | null) ?? null,
    reviewedByUserId:  (row.reviewed_by_user_id as string | null) ?? null,
    relationshipGroupId: (row.relationship_group_id as string | null) ?? null,
  };
}

// ── Cluster Queries ───────────────────────────────────────────────────────────

export async function getActiveClusters(workspaceId: string): Promise<{
  data: TwinsCluster[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(CLUSTERS_TABLE)
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('status', 'active')
      .order('created_at', { ascending: false });

    if (error) return { data: [], error: error.message };
    return { data: (data ?? []).map(r => rowToCluster(r as Record<string, unknown>)), error: null };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function getClusterHistory(workspaceId: string, limit = 50): Promise<{
  data: TwinsCluster[];
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(CLUSTERS_TABLE)
      .select('*')
      .eq('workspace_id', workspaceId)
      .in('status', ['accepted', 'dismissed'])
      .order('reviewed_at', { ascending: false })
      .limit(limit);

    if (error) return { data: [], error: error.message };
    return { data: (data ?? []).map(r => rowToCluster(r as Record<string, unknown>)), error: null };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * Propose a cluster through the one write path (propose_item_cluster): two or
 * more live items, named by the agent that found them. The same members open
 * already returns that proposal instead of a second one.
 */
export async function createTwinsCluster(
  workspaceId: string,
  cluster: Pick<TwinsCluster, 'clusterType' | 'itemIds' | 'rationale'>,
  agentType = 'twins',
): Promise<{ data: TwinsCluster | null; existing?: boolean; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('propose_item_cluster', {
      p_workspace_id: workspaceId,
      p_agent_type: agentType,
      p_cluster_type: cluster.clusterType,
      p_item_ids: cluster.itemIds,
      p_rationale: cluster.rationale,
    });
    if (error) return { data: null, error: error.message };
    const result = (data ?? {}) as { success?: boolean; existing?: boolean; error?: string; cluster?: Record<string, unknown> };
    if (result.success !== true || !result.cluster) return { data: null, error: result.error ?? 'The cluster could not be proposed.' };
    return { data: rowToCluster(result.cluster), existing: result.existing === true, error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function acceptCluster(clusterId: string, userId: string): Promise<{
  data: TwinsCluster | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('accept_twins_cluster', {
      p_cluster_id: clusterId,
      p_user_id:    userId,
    });

    if (error) return { data: null, error: error.message };
    if (!data)  return { data: null, error: 'Cluster not found or already reviewed' };

    return { data: rowToCluster(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function dismissCluster(clusterId: string, userId: string): Promise<{
  data: TwinsCluster | null;
  error: string | null;
}> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('dismiss_twins_cluster', {
      p_cluster_id: clusterId,
      p_user_id:    userId,
    });

    if (error) return { data: null, error: error.message };
    if (!data)  return { data: null, error: 'Cluster not found or already reviewed' };

    return { data: rowToCluster(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}
